import { reactive, toRaw } from "vue";
import { DEFAULT_LEGEND } from "../defaultLegend";
import type { Legend } from "../types/legend.types";
import Settings from "@/stores/Settings";
import { Facade } from "@/drawing";
import _ from "lodash";
import ScaleMenuState from "./ScaleMenuState";
import NoteMenuState from "./NoteMenuState";
import DraggerMenuState from "./DraggerMenuState";
import DraggerOptionState from "./DraggerOptionState";
import $ls, { LocalStorageKey } from "@/utils/local-storage";
import type { PhotoMarkerShape, DimensionKind } from "@/drawing";
import { LoadingTimer } from "@/utils/LoadingTimer";
import { MemoryMonitor } from "@/utils/MemoryMonitor";
import TemplateBlocksState from "./TemplateBlocksState";

export type StoredSettings = {
  legendIdx?: number;
  scale?: {
    name?: string;
    isTextsizeForced?: boolean;
    forcedSize?: number | "";
  };
  note?: {
    activeCategories?: string[];
    activeNoteIdx?: number;
    previousNoteIdx?: number;
  };
  dragger?: {
    name?: string;
    /** @deprecated remplacé par lineweightLevel, lu seulement pour migrer */
    isLwActive?: boolean;
    /** Multiplicateur d'épaisseur des traits à l'écran (0 = masquées). */
    lineweightMultiplier?: number;
    // Supports both legacy ("arrow" | "circle") and new PhotoMarkerShape format
    photoMarkerShape?: "arrow" | "circle" | PhotoMarkerShape;
    lockNativeElements?: boolean;
    label?: boolean;
    origin?: [number, number, number];
    closeContour?: boolean;
    shapeRecognition?: boolean;
    sequentialPointMode?: boolean;
    /** Ce que mesure l'outil cote : X, Y ou distance vraie. */
    dimensionKind?: DimensionKind;
    smoothen?: number;
    /** @deprecated replaced by smoothen === 0, only read to migrate old settings */
    smoothenEnabled?: boolean;
    activeBlock?: {
      name: string;
    };
    handlesSize?: number;
    eraserSize?: number;
    additiveSelection?: boolean;
    /** @deprecated group selection mode removed (always merged), left in old settings */
    groupSelection?: boolean;
    rulerAnchor?: { hY: number; vX: number };
    rulerVisible?: boolean;
    /** Épaisseur des règles (cf. RulerState). */
    rulerSize?: string;
  };
  snap?: {
    isOrthoActive: boolean;
    polar: {
      isActive: boolean;
      polarAngle: string; // Handle polar angle updates
    };
    isObjectSnapActive: boolean;
    objectSnapModes?: string[];
  };
};

type CanvasSettingState = {
  drawingId: number;
  legends: Legend[];
  activeLegend: Legend;
  storedSettings: StoredSettings | null;
  initialize: (drawingId: number, legends: Legend[]) => void;
  onDrawingOpen: (options?: { skipLineweight?: boolean }) => void;
  onLegendChange: (legend: Legend) => void;
  initLegend: () => void;
  correctLegendColors: (legend: Legend) => void;
  reapplyThemeToLegend: () => void;
  setActiveLegend: (legend: Legend) => void;
  isDrawingOpen: () => boolean;
  updateAndPersist: () => void;
  reset: () => void;
};

export default reactive<CanvasSettingState>({
  drawingId: -1,
  legends: [],
  activeLegend: DEFAULT_LEGEND,
  storedSettings: null,
  initialize(drawingId: number, legends: Legend[]) {
    this.drawingId = drawingId;
    this.legends = legends;
    const data = $ls.getItem(LocalStorageKey.CANVAS_SETTINGS);
    if (data) {
      this.storedSettings = JSON.parse(data) as StoredSettings;
      ScaleMenuState.initialize(drawingId, this.storedSettings);
      NoteMenuState.initialize(this.storedSettings);
      DraggerMenuState.initialize(this.storedSettings);
      DraggerOptionState.initialize(this.storedSettings);
    } else {
      ScaleMenuState.initialize(drawingId, null);
    }
  },
  async onDrawingOpen(options: { skipLineweight?: boolean } = {}) {
    LoadingTimer.step("initLegend");
    this.initLegend(); // start by setting legend

    // Pass layer configs to Facade (initLegend may not trigger setActiveLegend if Facade not ready)
    if (Facade.isInitialized) {
      LoadingTimer.step("setLayerConfigs");
      Facade.noteConfig.setLayerConfigs(toRaw(this.activeLegend.layer_configs));
    }

    // Then set scale menu
    LoadingTimer.step("ScaleMenuState.onDrawingOpen");
    ScaleMenuState.onDrawingOpen(this.activeLegend.scale_configs);

    LoadingTimer.step("NoteMenuState.onDrawingOpen");
    NoteMenuState.onDrawingOpen(this.activeLegend.note_configs);
    // update note config to take into account the previous changes
    NoteMenuState.updateComputedNoteConfig();

    LoadingTimer.step("DraggerMenuState.onDrawingOpen start");
    DraggerMenuState.onDrawingOpen({ skipLineweight: options.skipLineweight });

    LoadingTimer.step("DraggerOptionState.onDrawingOpen");
    DraggerOptionState.onDrawingOpen();

    MemoryMonitor.baseline(this.drawingId);
    MemoryMonitor.startSampling(60_000, this.drawingId);
  },
  initLegend() {
    if (
      this.storedSettings?.legendIdx &&
      this.legends[this.storedSettings?.legendIdx]
    ) {
      this.setActiveLegend(this.legends[this.storedSettings?.legendIdx]);
    } else {
      if (this.legends.length) {
        this.setActiveLegend(this.legends[0]);
      } else {
        this.setActiveLegend(DEFAULT_LEGEND);
      }
    }
  },
  setActiveLegend(legend: Legend) {
    this.activeLegend = legend;
    TemplateBlocksState.loadFromLegend(legend); // fire-and-forget
    const legendIdx = this.legends.findIndex(
      (legend) => legend == this.activeLegend,
    );
    // FIXME : How to remove casting here ?
    this.storedSettings = _.set(
      this.storedSettings || {},
      "legendIdx",
      legendIdx,
    ) as StoredSettings;
    // change colors that are similar to the theme
    this.correctLegendColors(this.activeLegend);
    // Pass layer configs to Facade for layer creation
    if (Facade.isInitialized) {
      Facade.noteConfig.setLayerConfigs(toRaw(legend.layer_configs));
    }
  },
  onLegendChange(legend: Legend) {
    this.setActiveLegend(legend);
    ScaleMenuState.onLegendChange(legend.scale_configs);
    NoteMenuState.onLegendChange(legend.note_configs);
    // Ensure the current photo marker shape is valid for the new legend
    DraggerMenuState.ensureValidPhotoMarkerShape();
    DraggerMenuState.persistPhotoMarker();
    //NoteMenuState.updateComputedNoteConfig();
    this.updateAndPersist();
  },
  correctLegendColors(legend: Legend) {
    const themeColor = Settings.actviveTheme.bgColor.slice(0, 3).toString();
    const themeOverrideColor = Settings.actviveTheme.overrideColor
      .slice(0, 3)
      .toString();
    for (const note_config of legend.note_configs) {
      if (note_config.props.color && note_config.props.color === themeColor) {
        note_config.props.color = themeOverrideColor;
      }
    }
    for (const layer_config of legend.layer_configs) {
      if (layer_config.color && layer_config.color === themeColor) {
        layer_config.color = themeOverrideColor;
      }
    }
  },
  /**
   * Fond du plan changé en direct (cf. PlanBackgroundState) : la légende
   * reprend la correction du nouveau fond, comme à l'ouverture d'un plan.
   */
  reapplyThemeToLegend() {
    this.correctLegendColors(this.activeLegend);
    if (Facade.isInitialized) {
      Facade.noteConfig.setLayerConfigs(toRaw(this.activeLegend.layer_configs));
    }
    NoteMenuState.updateComputedNoteConfig();
  },
  isDrawingOpen() {
    return Facade.isInitialized && Facade.file.isOpen();
  },
  /**
   * Add textsize to computed config while keeping the textstyle,
   * @param height undefined is used to unforce
   * @returns
   */
  updateAndPersist() {
    if (this.storedSettings) {
      $ls.setItem(
        LocalStorageKey.CANVAS_SETTINGS,
        JSON.stringify(this.storedSettings),
      );
    }
  },
  reset() {
    this.storedSettings = {};
    this.updateAndPersist();
  },
});
